import React, { useState, useEffect } from 'react';
// Common Components (Corrected Paths)
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
// API & Utils (Corrected Paths)
import { getRates, fetchRate, destroyRate, destroyRatesMultiple } from '../../../api/RatesAPI';
import { handleApiError } from '../../../api/risapi';
// Report component if needed
// import RatesReport from './RatesReport';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns'; // Ensure parseISO is imported if used elsewhere

const RatesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Rate');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [reportLoading, setReportLoading] = useState(false);
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    const [enableMultipleItemSelect, setEnableMultipleItemSelect] = useState(false);
    const [selectedRateIds, setSelectedRateIds] = useState(new Set());

    const [ratesList, setRatesList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false);

    // --- Navigation ---
    const handleAddClick = () => navigate('/franchisee_user/masters/rates/create');
    const handleEditClick = (id) => navigate(`/franchisee_user/masters/rates/edit/${id}`);

    // --- Delete Logic ---
    const handleDeleteClick = (id) => {
        fetchRate(id, function (result) {
            if (result.rate) {
                const deleteItem = result.rate;
                Swal.fire({
                    title: 'Are you sure?',
                    html: `
                        <p>Delete Rate for:</p>
                        <ul class="delete-details-list">
                            <li><strong>Product:</strong> ${deleteItem.product?.name || 'N/A'}</li>
                            <li><strong>Unit:</strong> ${deleteItem.uom?.name || 'N/A'}</li>
                            <li><strong>Type:</strong> ${deleteItem.rate_type?.name || 'N/A'}</li>
                            <li><strong>Base Price:</strong> ${deleteItem.base_price}</li>
                            <li><strong>Effective From:</strong> ${deleteItem.with_effect_from}</li>
                        </ul>
                        <p>This action cannot be undone.</p>`,
                    icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#6c757d', confirmButtonText: 'Yes, delete it!'
                }).then((dialogResult) => {
                    if (dialogResult.isConfirmed) {
                        destroyRate(deleteItem, function (apiResponse) {
                            if (apiResponse.rate) {
                                Swal.fire('Deleted!', 'Record deleted.', 'success');
                                setSaveCompleted(prev => !prev);
                            } else {
                                Swal.fire('Error!', apiResponse.error?.response?.data?.message || 'Could not delete rate. It might be in use.', 'error');
                            }
                        });
                    }
                });
            } else { Swal.fire('Error!', 'Could not fetch rate details.', 'error'); }
        });
    };

    // --- Multi-Delete Logic ---
     const showMultipleDeleteConfirmation = () => {
         const deletingItems = ratesList.data.filter(item => selectedRateIds.has(item.id));
         if (deletingItems.length === 0) {
              Swal.fire('No Selection', 'Please select at least one Rate to delete.', 'info');
              return;
         }
         Swal.fire({
             title: `Delete ${deletingItems.length} Rates?`,
             html: `<p>You are about to delete ${deletingItems.length} selected rate(s).</p><p class="mt-2 text-danger">This action cannot be undone.</p>`,
             icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#6c757d', confirmButtonText: 'Yes, delete them!'
         }).then((result) => {
             if (result.isConfirmed) {
                 const idsToDelete = Array.from(selectedRateIds);
                 destroyRatesMultiple(idsToDelete, function (response) {
                     if (response.rate) {
                         Swal.fire('Deleted!', 'Selected records deleted.', 'success');
                         setEnableMultipleItemSelect(false);
                         setSelectedRateIds(new Set());
                         setSaveCompleted(prev => !prev);
                     } else {
                         Swal.fire('Error!', response.error?.response?.data?.message || 'Could not delete selected rates. Some might be in use.', 'error');
                     }
                 });
             }
         });
     };

    // --- Checkbox Handler ---
    const handleCheckboxChange = (id, isChecked) => {
        setSelectedRateIds(prev => {
            const newSet = new Set(prev);
            if (isChecked) newSet.add(id); else newSet.delete(id);
            return newSet;
        });
    };

    // --- Effects ---
    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        document.title = 'Manage Rates';
        let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
        setViewDataLoading(true);
        setSelectedRateIds(new Set());
        setEnableMultipleItemSelect(false);
        getRates(filters, function (response) {
            setViewDataLoading(false);
            if (response.rates) {
                let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                if (Array.isArray(response.rates)) { data = response.rates; }
                else if (response.rates.data && Array.isArray(response.rates.data)) {
                    data = response.rates.data; currentP = response.rates.current_page; lastP = response.rates.last_page; responsePP = response.rates.per_page;
                } else { console.error("Unexpected API structure:", response.rates); handleApiError(response); return; }
                setRatesList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP });
            } else {
                 handleApiError(response);
                 setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Rates.' });
                 setShowToaster(true);
                 setRatesList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
            }
        });
    }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    // Helper to format date or show placeholder
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            // Add time part to avoid timezone issues with date-only strings
            return format(new Date(dateString + 'T00:00:00'), 'dd-MMM-yyyy');
        } catch (error) {
            return dateString; // Fallback
        }
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Rates List'>
                        <li><a className="f-s-14 f-w-500" href="#">Masters</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Rates</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active" data-tab={1}>All Rates</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                            <button className="text-nowrap btn btn-red btn-sm ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="ph-bold  ph-circles-three-plus"></i> Add Rate
                                            </button>
                                            {/* Add RatesReport component here */}
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper" id="card-container">
                                <div className="d-flex flex-wrap m-md-3">
                                    {ratesList.data.length > 0 ? (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={ratesList.current_page} totalPages={ratesList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                            <div className='d-flex align-items-center ms-auto mt-2 mt-md-0'>
                                                <button className="btn btn-info btn-sm me-2" type="button" onClick={() => setEnableMultipleItemSelect(prev => !prev)}>
                                                    {enableMultipleItemSelect ? (<><i className="fa-solid fa-check fa-fw me-1" />Done</>) : 'Select'}
                                                </button>
                                                {enableMultipleItemSelect && selectedRateIds.size > 0 && (
                                                    <button className="btn btn-danger btn-sm" type="button" onClick={showMultipleDeleteConfirmation}> Delete ({selectedRateIds.size}) Records </button>
                                                )}
                                            </div>
                                        </>
                                    ) : ( !viewDataLoading && <p className="text-center w-100">No Rates found.</p> )}
                                    {(reportLoading || viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                {/* --- Card Layout --- */}
                                <div className="tabs-content active" id="tab-1">
                                    <div className="row">
                                        {!viewDataLoading && ratesList.data.map((rate) => (
                                            <div className="col-md-6 col-xl-4 project-card" key={rate.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            {enableMultipleItemSelect && (
                                                                <input className="form-check-input me-2" type="checkbox" checked={selectedRateIds.has(rate.id)} onChange={(e) => handleCheckboxChange(rate.id, e.target.checked)} id={`select-rate-${rate.id}`} />
                                                            )}
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden me-2 btn-light-reddit">
                                                                 <i className="ti ti-receipt-2 f-s-18"></i>
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600">{rate.product?.name || 'N/A'}</h6>
                                                                <small className="text-muted d-block">{rate.rate_type?.name || 'N/A'} - {rate.uom?.name || 'N/A'}</small>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(rate.id)}><i className="ti ti-edit text-success me-1" /> Edit</a></li>
                                                                    <li><button className="dropdown-item delete-button" onClick={() => handleDeleteClick(rate.id)}><i className="ti ti-trash text-danger me-1" /> Delete</button></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body py-2">
                                                         <div className="d-flex justify-content-between align-items-center">
                                                             <div>
                                                                  <p className="f-w-500 text-secondary mb-0">Base Price</p>
                                                                  <h6 className="f-w-600 mb-0">₹{parseFloat(rate.base_price || 0).toFixed(2)}</h6>
                                                             </div>
                                                              <div className='text-end'>
                                                                  <p className="f-w-500 text-secondary mb-0">Effective From</p>
                                                                  <h6 className="f-w-600 mb-0">{formatDate(rate.with_effect_from)}</h6>
                                                             </div>
                                                         </div>
                                                          {/* Display Final Price if calculated and different from base price */}
                                                          {rate.calculated_final_price !== undefined && parseFloat(rate.calculated_final_price).toFixed(2) !== parseFloat(rate.base_price || 0).toFixed(2) && (
                                                             <div className="mt-1 pt-1 border-top text-end">
                                                                 <small className="text-muted">Final Price (inc. tax): ₹{parseFloat(rate.calculated_final_price).toFixed(2)}</small>
                                                             </div>
                                                         )}
                                                     </div>
                                                </div>
                                            </div>
                                        ))}
                                        {/* No Data Card */}
                                        {!viewDataLoading && ratesList.data.length === 0 && (
                                            <div className="col-12"> <div className="card mb-3"><div className="row g-0"><div className="col-md-12"><div className="card-body"><h5 className="card-title">No Rates Found</h5><p className="card-text">No records match your criteria or no rates added yet.</p></div></div></div></div></div>
                                        )}
                                    </div>
                                </div>
                                {/* --- Card Layout End --- */}

                                {/* <div className="d-flex flex-wrap m-md-3">
                                     {ratesList.data.length > 0 && (
                                         <AppPaginator entityName={entityName} currentPage={ratesList.current_page} totalPages={ratesList.tot_page} setCurrentPage={setCurrentPage} />
                                     )}
                                 </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default RatesPage;